import React from 'react';
import { Check, Search, ShieldAlert, AlertTriangle } from 'lucide-react';

// ============================================================================
// CHA-272 — canonical Carbon UI states (design: ChainTo States - Patterns)
// ============================================================================
// The six states every reskinned module uses — no improvised spinners or
// empty states (design rule). Copy pattern: what happened → what the system
// did → what you can do. All flat, square, Plex.

// 01 LOADING — shimmer skeleton rows (inline spinners are reserved for
// in-flight actions, not page loads).
export function SkeletonRows({ rows = 4 }) {
  return (
    <div data-testid="carbon-skeleton" className="px-[18px] py-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3.5 py-3 border-t border-carbon-gray-10 first:border-t-0">
          <div className="w-[58px] h-6 bg-carbon-gray-10 animate-pulse" />
          <div className="flex-1">
            <div className="h-3.5 w-2/5 bg-carbon-gray-10 animate-pulse mb-1.5" />
            <div className="h-3 w-3/5 bg-carbon-gray-10 animate-pulse" />
          </div>
          <div className="w-20 h-4 bg-carbon-gray-10 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

// 02 EMPTY — GOOD NEWS ("Queue clear.")
export function EmptyGoodNews({ title = 'Queue clear.', detail }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <div className="w-9 h-9 flex items-center justify-center bg-carbon-green-10 text-carbon-green-50 mb-3">
        <Check size={18} />
      </div>
      <div className="text-sm font-semibold text-carbon-ink">{title}</div>
      {detail ? <div className="text-xs text-carbon-gray-50 mt-1">{detail}</div> : null}
    </div>
  );
}

// 03 NO RESULTS — echo the query, offer the way out.
export function NoResults({ query, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <div className="w-9 h-9 flex items-center justify-center bg-carbon-gray-10 text-carbon-gray-50 mb-3">
        <Search size={16} />
      </div>
      <div className="text-sm font-semibold text-carbon-ink">
        No results{query ? <> for <span className="font-plex-mono">&ldquo;{query}&rdquo;</span></> : ''}
      </div>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-2.5 text-xs font-medium text-carbon-blue-60 hover:text-carbon-blue-70"
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
}

// 04 ERROR — RECOVERABLE (red left rail: what failed, what we're showing, retry)
export function ErrorBanner({ what, staleNote, onRetry }) {
  return (
    <div className="relative bg-carbon-white border border-carbon-border px-[18px] py-3 mb-4">
      <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-red-60" />
      <div className="flex items-start gap-3">
        <AlertTriangle size={15} className="text-carbon-red-60 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-carbon-ink">{what}</div>
          {staleNote ? <div className="text-xs text-carbon-gray-70 mt-0.5">{staleNote}</div> : null}
        </div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink px-3 py-1.5 hover:bg-carbon-gray-10 flex-shrink-0"
          >
            Retry
          </button>
        ) : null}
      </div>
    </div>
  );
}

// 05 PERMISSION DENIED — name the role and who grants it.
export function PermissionDenied({ requiredRole = 'admin', grantedBy = 'your admin' }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <div className="w-9 h-9 flex items-center justify-center bg-carbon-gray-10 text-carbon-gray-70 mb-3">
        <ShieldAlert size={17} />
      </div>
      <div className="text-sm font-semibold text-carbon-ink">You don&rsquo;t have access to this view</div>
      <div className="text-xs text-carbon-gray-50 mt-1">
        Requires the <span className="font-plex-mono">{requiredRole}</span> role — granted by {grantedBy}.
        Access is enforced server-side.
      </div>
    </div>
  );
}

// 06 AGENT DEGRADED — orange banner: which scan didn't run, what the queue reflects.
export function AgentDegraded({ text, onRunNow }) {
  return (
    <div className="relative bg-carbon-white border border-carbon-border px-[18px] py-3 mb-4">
      <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-orange-40" />
      <div className="flex items-start gap-3">
        <AlertTriangle size={15} className="text-carbon-orange-70 mt-0.5 flex-shrink-0" />
        <div className="flex-1 text-[13px] text-carbon-ink">{text}</div>
        {onRunNow ? (
          <button
            type="button"
            onClick={onRunNow}
            className="text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink px-3 py-1.5 hover:bg-carbon-gray-10 flex-shrink-0"
          >
            Run now
          </button>
        ) : null}
      </div>
    </div>
  );
}
